import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Store } from '@ngrx/store';
import {
  setEnterprises,
  triggerIsLoadingSelectedEnterprise,
} from 'src/store/enterprises/enterprises.actions';
import {
  IEnterprisesState,
  selectEnterprises,
  selectEnterprisesState,
} from 'src/store/enterprises/enterprises.state';

import { firstValueFrom, Observable } from 'rxjs';

import _ from 'lodash';

import { ApiService } from './api.service';
import { TranslationService } from 'lipp-components';
import { ToastService } from 'lipp-components';
import { ClientsService } from './clients.service';

import { HttpErrorMessage, Result } from 'src/models/Result';
import { ResultOfQueryFilter } from 'src/models/ResultOfQueryFilter';
import { Enterprise } from 'src/models/Enterprise';
import { FilterDefinition, IncludeDefinition } from 'lipp-components';

import { EnterpriseDTO } from 'src/dtos/EnterpriseDTO';
import { CreateEnterpriseDTO } from 'src/dtos/CreateEnterpriseDTO';
import { MinimalEnterpriseDTO } from 'src/dtos/MinimalEnterpriseDTO';

import { EnOperator } from 'lipp-components';

@Injectable({ providedIn: 'root' })
export class EnterpriseService {
  constructor(
    private api: ApiService,
    private store: Store,
    private translationService: TranslationService,
    private toastService: ToastService,
    private clientsService: ClientsService
  ) {}

  public getPartnerEnterpriseForCurrentUser(): Observable<Result<Enterprise>> {
    return new Observable<Result<Enterprise>>((observer) => {
      const res = new Result<Enterprise>();

      this.api
        .get<Result<EnterpriseDTO>>(
          'Clients/Enterprises/GetPartnerEnterpriseForCurrentUser'
        )
        .subscribe(
          (result) => {
            res.messages = result.messages;

            if (!res.ok) {
              observer.next(res);
              observer.complete();
              return;
            }

            res.value = new Enterprise(result.value!, []);
            observer.next(res);
            observer.complete();
            return;
          },
          (error: HttpErrorResponse) => {
            res.messages.push(new HttpErrorMessage(error.status));
            observer.next(res);
            observer.complete();
            return;
          }
        );
    });
  }

  public getEnterprisesForCurrentUser(): Observable<Result<Enterprise[]>> {
    return new Observable<Result<Enterprise[]>>((observer) => {
      const res = new Result<Enterprise[]>();

      // Todo

      // Todo: SubCollectionFilters and Expression, then type QueryFilter
      // Todo: orderBy für die sidebar?
      const queryFilter: any = {
        includes: [new IncludeDefinition('Stations.Modules.Module')],
        filters: [
          new FilterDefinition('IsActive', EnOperator.Equal, true),
          { Expression: 'Stations != null' },
        ],
        SubCollectionFilters: [
          { Path: 'Stations', Expression: 'IsActive == true' },
        ],
      };

      this.api
        .post<ResultOfQueryFilter<EnterpriseDTO>>(
          `Clients/Enterprises/GetEnterprisesForCurrentUser`,
          queryFilter
        )
        .subscribe(
          async (result) => {
            res.messages = result.messages;

            if (!res.ok) {
              observer.next(res);
              observer.complete();
              return;
            }

            const enterprises: Enterprise[] = [];

            const stationIds = result
              .value!.data.flatMap((dto) => dto.stations)
              .map((station) => station.id);

            const stationModulesForStationResult =
              await this.clientsService.getStationModulesForStationIds(
                stationIds
              );

            if (!stationModulesForStationResult.ok) {
              const error = stationModulesForStationResult.errors![0];

              res.messages.push({
                fullKey: error.key,
                key: error.key,
                messageType: 0,
                text: error.text,
                textParameters: error.textParameters,
                textRaw: error.text,
              });

              observer.next(res);
              observer.complete();
              return;
            }

            result.value!.data.forEach((enterprise) => {
              const stationModuleForEnterprise =
                stationModulesForStationResult.value!.filter((stationModule) =>
                  enterprise.stations.some(
                    (station) => stationModule.stationId === station.id
                  )
                );

              enterprises.push(
                new Enterprise(enterprise, stationModuleForEnterprise)
              );
            });

            res.value = enterprises;
            observer.next(res);
            observer.complete();
            return;
          },
          (error: HttpErrorResponse) => {
            res.messages.push(new HttpErrorMessage(error.status));
            observer.next(res);
            observer.complete();
            return;
          }
        );
    });
  }

  public getEnterprise(id: number): Observable<Result<Enterprise>> {
    return new Observable<Result<Enterprise>>((observer) => {
      const res = new Result<Enterprise>();
      // Todo

      // Todo: check filters, then type QueryFilter
      const queryFilter: any = {
        includes: [
          new IncludeDefinition('Stations.Modules.Module'),
          new IncludeDefinition('Currency'),
        ],
        filters: [
          new FilterDefinition('IsActive', EnOperator.Equal, true),
          { Expression: 'Stations != null' },
        ],
        SubCollectionFilters: [
          { Path: 'Stations', Expression: 'IsActive == true' },
          { Path: 'Stations.Modules.Module', Expression: 'IsActive == true' },
        ],
      };

      this.api
        .post<Result<EnterpriseDTO>>(
          `Clients/Enterprises/GetEnterprise?id=${id}`,
          queryFilter
        )
        .subscribe(
          async (result) => {
            res.messages = result.messages;

            if (!res.ok) {
              observer.next(res);
              observer.complete();
              return;
            }

            const stationIds = result.value!.stations.map(
              (station) => station.id
            );

            const stationModulesForStationResult =
              await this.clientsService.getStationModulesForStationIds(
                stationIds
              );

            if (!stationModulesForStationResult.ok) {
              const error = stationModulesForStationResult.errors![0];

              res.messages.push({
                fullKey: error.key,
                key: error.key,
                messageType: 0,
                text: error.text,
                textParameters: error.textParameters,
                textRaw: error.text,
              });

              observer.next(res);
              observer.complete();
              return;
            }

            res.value = new Enterprise(
              result.value!,
              stationModulesForStationResult.value!
            );

            observer.next(res);
            observer.complete();
            return;
          },
          (error: HttpErrorResponse) => {
            res.messages.push(new HttpErrorMessage(error.status));
            observer.next(res);
            observer.complete();
            return;
          }
        );
    });
  }

  public getEnterpriseForLippPortal(
    id: number
  ): Observable<Result<Enterprise | null>> {
    return new Observable<Result<Enterprise | null>>((observer) => {
      const res = new Result<Enterprise | null>();

      this.api
        .get<Result<EnterpriseDTO | null>>(
          `Clients/Enterprises/GetEnterpriseForLippPortal?id=${id}`
        )
        .subscribe(
          async (result) => {
            res.messages = result.messages;

            if (!res.ok) {
              observer.next(res);
              observer.complete();
              return;
            }

            if (result.value) {
              const stationIds = result.value!.stations.map(
                (station) => station.id
              );

              const stationModulesForStationResult =
                await this.clientsService.getStationModulesForStationIds(
                  stationIds
                );

              if (!stationModulesForStationResult.ok) {
                const error = stationModulesForStationResult.errors![0];

                res.messages.push({
                  fullKey: error.key,
                  key: error.key,
                  messageType: 0,
                  text: error.text,
                  textParameters: error.textParameters,
                  textRaw: error.text,
                });

                observer.next(res);
                observer.complete();
                return;
              }

              res.value = new Enterprise(
                result.value,
                stationModulesForStationResult.value!
              );
            }

            observer.next(res);
            observer.complete();
            return;
          },
          (error: HttpErrorResponse) => {
            res.messages.push(new HttpErrorMessage(error.status));
            observer.next(res);
            observer.complete();
            return;
          }
        );
    });
  }

  public getPartnerEnterprises(): Observable<Enterprise[]> {
    return new Observable((observer) => {
      this.api
        .get<Result<EnterpriseDTO[]>>(
          'Clients/Enterprises/GetPartnerEnterprises'
        )
        .subscribe(
          (result) => {
            if (result.ok && result.value) {
              const partnerEnterprises: Enterprise[] = new Array<Enterprise>();

              result.value.forEach((partnerEnterprise) => {
                partnerEnterprises.push(new Enterprise(partnerEnterprise, []));
              });

              observer.next(partnerEnterprises);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public createEnterprise(
    dto: CreateEnterpriseDTO
  ): Observable<Result<number>> {
    return new Observable<Result<number>>((observer) => {
      const res = new Result<number>();

      this.api
        .post<Result<number>>('Clients/Enterprises/CreateEnterprise', dto)
        .subscribe(
          (result) => {
            res.messages = result.messages;

            if (!res.ok) {
              observer.next(res);
              observer.complete();
              return;
            }

            res.value = result.value;
            observer.next(res);
            observer.complete();
            return;
          },
          (error: HttpErrorResponse) => {
            res.messages.push(new HttpErrorMessage(error.status));
            observer.next(res);
            observer.complete();
            return;
          }
        );
    });
  }

  public getEnterpriseByStationIdForLippPortal(
    stationId: number
  ): Observable<Result<Enterprise | null>> {
    return new Observable<Result<Enterprise | null>>((observer) => {
      const res = new Result<Enterprise | null>();

      this.api
        .get<Result<EnterpriseDTO | null>>(
          `Clients/Enterprises/GetEnterpriseByStationIdForLippPortal?stationId=${stationId}`
        )
        .subscribe(
          async (result) => {
            res.messages = result.messages;

            if (!res.ok) {
              observer.next(res);
              observer.complete();
              return;
            }

            if (result.value) {
              const stationIds = result.value!.stations.map(
                (station) => station.id
              );

              const stationModulesForStationResult =
                await this.clientsService.getStationModulesForStationIds(
                  stationIds
                );

              if (!stationModulesForStationResult.ok) {
                const error = stationModulesForStationResult.errors![0];

                res.messages.push({
                  fullKey: error.key,
                  key: error.key,
                  messageType: 0,
                  text: error.text,
                  textParameters: error.textParameters,
                  textRaw: error.text,
                });

                observer.next(res);
                observer.complete();
                return;
              }

              res.value = new Enterprise(
                result.value,
                stationModulesForStationResult.value!
              );
            }

            observer.next(res);
            observer.complete();
            return;
          },
          (error: HttpErrorResponse) => {
            res.messages.push(new HttpErrorMessage(error.status));
            observer.next(res);
            observer.complete();
            return;
          }
        );
    });
  }

  public async reloadEnterpriseOfStation(stationId: number): Promise<void> {
    const enterprises = (await firstValueFrom(
      this.store.select(selectEnterprises)
    ))!;

    const currentEnterprise = enterprises.find((enterprise) =>
      enterprise.stations.some((station) => station.id === stationId)
    )!;

    const enterpriseId: number = currentEnterprise.id;

    const previousEnterprisesState: IEnterprisesState = await firstValueFrom(
      this.store.select(selectEnterprisesState)
    );

    this.store.dispatch(triggerIsLoadingSelectedEnterprise());

    const enterpriseResult = await firstValueFrom(
      this.getEnterpriseForLippPortal(enterpriseId)
    );

    if (!enterpriseResult.ok) {
      this.store.dispatch(
        setEnterprises({ enterprises: previousEnterprisesState.enterprises! })
      );

      this.toastService.showAlert(
        this.translationService.translate('Enterprises.ErrorLoadingEnterprise'),
        this.translationService.translate(enterpriseResult.errors![0].key)
      );

      return;
    }

    const enterprise: Enterprise = enterpriseResult.value!;

    const newEnterprises: Enterprise[] = _.cloneDeep(
      previousEnterprisesState.enterprises!
    );

    const index = newEnterprises.findIndex(
      (enterprise) => enterprise.id === enterpriseId
    );

    newEnterprises.splice(index, 1);

    newEnterprises.splice(index, 0, enterprise);

    this.store.dispatch(setEnterprises({ enterprises: newEnterprises }));
  }

  public getEnterprisesWithLippPOSBMDModule(): Observable<
    MinimalEnterpriseDTO[]
  > {
    return new Observable((observer) => {
      this.api
        .get<Result<MinimalEnterpriseDTO[]>>(
          'Clients/Enterprises/GetEnterprisesWithLippPOSBMDModule'
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(result.value!);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }
}
